/** English (default). Other locales must provide exactly the same keys — enforced by the `Messages` type. */
export const en = {
  app: {
    name: 'Salt & Shot',
    shortName: 'Salt & Shot',
    subtitle: 'Naval Tactics',
    tagline: 'A game of tactics under sail and steam',
    skipToContent: 'Skip to content',
    loading: 'Taking soundings…',
  },
  nav: {
    label: 'Main navigation',
    profile: 'Profile',
    logbook: 'Logbook',
    harbour: 'Harbour',
    newGame: 'New Game',
    comingSoon: 'coming soon',
    newGameFailed: 'The game could not be started. Try again.',
    signIn: 'Sign in',
    join: 'Join the Fleet',
    signOut: 'Sign out',
    language: 'Language',
  },
  landing: {
    pageTitle: 'Salt & Shot',
    chartTitle: 'A New and Correct Chart of',
    chartSubtitle: 'Tactics under Sail & Steam',
    lead: 'Command a flotilla of brigs, frigates and paddle steamers. Read the wind, mind the reefs and the guns of the forts, and bring your ships home to the Royal Port.',
    features: {
      wind: 'The wind spinner decides who sails and who must beat to windward.',
      steam: 'Sail and early steam in one fleet: speed against weather gauge.',
      ports: 'Forts, ports and the Royal Port shape every engagement.',
    },
    playGuest: 'Play against the computer',
    playGuestNote: 'No account needed: the small map, against the computer.',
    published: 'Engraved for the Admiralty of no particular nation',
  },
  profile: {
    pageTitle: "Captain's profile",
    title: "Captain's profile",
    particulars: "Captain's particulars",
    serviceSince: 'In the service since {{date}}',
    lastSeen: 'Last seen {{date}}',
    email: 'E-mail',
    emailUnknown: 'No e-mail on record',
    register: "Ship's register",
    registerHint: 'How other captains will see you in the fleet lists.',
    nickname: 'Nickname',
    nicknameHint: '3–20 letters, digits, “_” or “-”. Must be unique in the fleet.',
    displayName: 'Name',
    displayNameHint: 'Shown next to your nickname. Up to 40 characters.',
    save: 'Enter in the register',
    saving: 'Inking…',
    saved: 'Entered in the register.',
    languageTitle: 'Language',
    languageHint: 'The language of charts, logs and orders. Saved to your profile.',
    loadFailed: 'Could not fetch your papers. The signal may be lost — try again shortly.',
    retry: 'Try again',
  },
  avatars: {
    'pennant-yellow': 'Yellow pennant',
    'pennant-green': 'Green pennant',
    'pennant-crimson': 'Crimson pennant',
    'pennant-slate': 'Slate pennant',
    'burgee-gold': 'Gold burgee',
    'burgee-blue': 'Blue burgee',
    'signal-alpha': 'Signal flag Alpha',
    'signal-bravo': 'Signal flag Bravo',
    'signal-delta': 'Signal flag Delta',
    'signal-kilo': 'Signal flag Kilo',
    'signal-november': 'Signal flag November',
    'signal-papa': 'Signal flag Papa',
    'signal-uniform': 'Signal flag Uniform',
    'signal-zulu': 'Signal flag Zulu',
  },
  newGame: {
    title: 'New game',
    close: 'Close',
    how: 'How to play',
    fleet: { red: 'Your fleet', blue: "Opponent's fleet", colour: 'Colour' },
    bot: { name: 'Against the computer', hint: 'You command the first fleet; the computer answers at once.' },
    online: {
      name: 'With a friend online',
      hint: 'You get a link to send; the game begins when they take it.',
    },
    hotseat: { name: 'Two at one screen', hint: 'Both fleets on this device; pass it over each turn.' },
  },
  logbook: {
    pageTitle: 'Logbook',
    title: 'Logbook',
    lead: 'Your voyages and engagements, entered by date.',
    emptyTitle: 'Riding at anchor',
    emptyBody:
      'No voyages have been entered in the log yet. Start a game, and your engagements will be recorded here.',
    columns: {
      opponent: 'Opponent',
      status: 'Status',
      turn: 'Move',
      updated: 'Last entry',
    },
    openInvite: 'Awaiting an opponent',
    hotseat: 'Hot seat: both fleets',
    bot: 'Against the computer',
    open: 'Open',
    status: {
      pending: 'Mustering',
      active: 'Under way',
      finished: 'Concluded',
      abandoned: 'Abandoned',
    },
    turn: {
      mine: 'Your move',
      theirs: 'Their move',
      none: '—',
    },
    side: {
      yellow: 'Yellow fleet',
      green: 'Green fleet',
    },
  },
  harbour: {
    pageTitle: 'Harbour',
    title: 'Welcome aboard',
    welcome: 'Welcome aboard, {{rank}} {{name}}',
    watches: {
      middle: 'Middle watch',
      morning: 'Morning watch',
      forenoon: 'Forenoon watch',
      afternoon: 'Afternoon watch',
      firstDog: 'First dog watch',
      lastDog: 'Last dog watch',
      first: 'First watch',
    },
    bells: {
      b1: 'one bell',
      b2: 'two bells',
      b3: 'three bells',
      b4: 'four bells',
      b5: 'five bells',
      b6: 'six bells',
      b7: 'seven bells',
      b8: 'eight bells',
    },
    ranks: {
      midshipman: 'Midshipman',
      lieutenant: 'Lieutenant',
      commander: 'Commander',
      captain: 'Captain',
      commodore: 'Commodore',
      rearAdmiral: 'Rear-Admiral',
    },
    helm: {
      kicker: 'At the helm',
      yourMove: 'Your orders are awaited',
      theirMove: 'The enemy is to move',
      turn: 'turn {{n}}',
      lastEntry: 'Last entry in the log {{when}}.',
      take: 'Take the helm',
      look: 'Look over the board',
      more: 'Other games awaiting your orders: {{n}}',
      calmTitle: 'All quiet on deck',
      calmBody: 'No engagement is under way. Choose your fleets and put to sea.',
    },
    record: {
      title: 'Service record',
      voyages: 'Voyages',
      underWay: 'Under way',
      concluded: 'Concluded',
      rank: 'Rank',
      next: 'Next: {{rank}}, after {{n}} engagements concluded.',
      openLog: 'Open the logbook',
    },
    order: {
      title: 'Order of the day',
      signed: 'the Admiral',
      next: 'Next order',
    },
    orders: {
      o1: 'Muster only the ships you mean to sail: those left out of the squadron hold their place, and may still fire.',
      o2: 'A fresh wind is fastest downwind; every point you haul into it costs you a cell.',
      o3: 'Give a fort a wide berth unless you mean to silence its guns.',
      o4: 'A damaged ship in her own port may go into dock, if she has not fired: she mends a hole each turn.',
      o5: 'Mind the reefs marked red on the chart: no keel passes over them.',
      o6: 'Hover over an enemy before you sail: the chart shows which of your ships can reach her with their guns.',
      o7: 'The enemy’s Royal Port is the prize. Keep a ship or two in hand to guard your own.',
      o8: 'Steam owes nothing to the wind: keep a steamer by you for the day the breeze fails.',
      o9: 'A volley rolls once for every gun in it: a hit lands every hole at once, a miss wastes them all. Use it to sink a big ship at a blow.',
      o10: 'Shots not fired are lost when the turn ends: if a target is in range, take it.',
      o11: 'Near reefs, sail a small squadron: a storm drives only the ships you mustered, and a reef wrecks whoever it catches.',
      o12: 'A ship holding the enemy’s Royal Port cannot fire. Leave her out of the squadron and no storm will drive her off.',
      o13: 'A fallen fort is not lost for good: crew it with your own ships, with no enemy in the port, and it flies your flag.',
      o14: 'If a fort falls, every ship in its dock goes down with it. Dock only behind a fort that will stand.',
      o15: 'Land and forts block a shot; ships and reefs do not. Find a clear line before you close in.',
      o16: 'In calm the sails hang idle, but oars and steam go in any direction: a calm is the galley’s hour.',
      o17: 'Two ships to a sea cell, one to a coast cell: choose your berth before you sail, or you will stop short of it.',
      o18: 'While its fort stands, no enemy enters your port: a whole fort is the best gate.',
    },
  },
  invite: {
    against: 'against',
    waiting: {
      pageTitle: 'Awaiting an opponent',
      kicker: 'Online game',
      title: 'Awaiting an opponent',
      body: 'Send this link to the captain you want to fight. The game opens here as soon as they take it.',
      linkLabel: 'Invite link',
      copy: 'Copy the link',
      copied: 'Copied',
      expires: 'The link is valid until {{date}}.',
      notYours: 'Waiting for the second captain to take the invite.',
    },
    join: {
      pageTitle: 'An invite to battle',
      kicker: 'Online game',
      title: 'An invite to battle',
      body: 'Captain {{name}} challenges you to an engagement. You will command the second fleet.',
      own: 'This is your own invite: send the link to the captain you want to fight.',
      signIn: 'Sign in to accept',
      signUp: 'Join the fleet',
      accept: 'Take command',
      open: 'Open the game',
      home: 'To the title page',
      missing: 'There is no such invite. Check the link.',
      taken: 'Another captain has already taken this invite.',
      expired: 'This invite is no longer valid. Ask for a new link.',
      failed: 'The invite could not be accepted. Please try again.',
    },
  },
  notFound: {
    pageTitle: 'Uncharted Waters',
    title: 'Uncharted Waters',
    body: 'No soundings have been taken here, and this page is not on any chart in our possession.',
    back: 'Return to port',
  },
  error: {
    title: 'Rough seas',
    body: 'Something went amiss aboard. Please try again.',
    reload: 'Reload',
    authTitle: 'Could not come aboard',
    authBody: 'Signing in did not complete. Please try again.',
  },
  errors: {
    nickname_taken: 'Another captain already flies under this nickname.',
    validation_failed: 'Please check the highlighted entries.',
    unauthorized: 'Your session has ended. Please sign in again.',
    network: 'No answer from the shore. Check your connection and try again.',
    generic: 'Something went amiss. Please try again.',
    nickname_charset: 'Use letters, digits, “_” or “-” only.',
    too_short: 'Too short.',
    too_long: 'Too long.',
  },
  ships: {
    names: {
      first_rate: 'First rate, 100 guns',
      ship_of_line: 'Ship of the line, 60 guns',
      sail_frigate: 'Sailing frigate',
      brig: 'Brig',
      cutter: 'Cutter',
      galleon: 'Galleon',
      galley: 'Galley',
      ironclad_battery: 'Ironclad battery',
      steam_frigate: 'Steam frigate',
      steam_corvette: 'Steam corvette',
      paddle_steamer: 'Paddle steamer',
      monitor: 'Monitor',
    },
    guns: {
      first_rate: '100 guns',
      ship_of_line: '60 guns',
      sail_frigate: '40 guns',
      brig: '14 guns',
      cutter: '8 guns',
      galleon: '30 guns',
      galley: '8 guns',
      ironclad_battery: '40 heavy guns',
      steam_frigate: '30 guns',
      steam_corvette: '20 guns',
      paddle_steamer: '10 guns',
      monitor: '2 heavy turret guns',
    },
    sides: {
      red: 'Yellow fleet',
      blue: 'Green fleet',
    },
    token: {
      label: '{{ship}}, {{nation}}',
      damage: '{{damage}} of {{max}} holes',
      sunk: 'sunk',
      anchored: 'at anchor',
      repairing: 'under repair',
    },
    card: {
      label: '{{ship}} card',
      alt: 'Watercolour illustration: {{ship}}',
      stats: 'Characteristics',
      fresh: 'Fresh wind',
      calm: 'Calm',
      storm: 'Storm drift',
      cannotMove: 'does not move',
      shots: 'Shots per turn',
      range: 'Range',
      sinksAt: 'Holes to sink',
      holes: 'Holes',
      status: {
        sunk: 'Sunk',
        repairing: 'In dock, under repair',
        anchored: 'At anchor in port',
        repairingFort: 'Repairing a fort',
        ready: 'Ready',
      },
    },
  },
  game: {
    load: {
      failed: 'The game could not be loaded.',
      notFound: 'There is no such game in your logbook.',
      retry: 'Try again',
      back: 'To the logbook',
    },
    turn: 'Turn {{n}}',
    toAct: '{{side}} to act',
    phase: {
      handover: 'Handover',
      replay: 'Replay',
      waiting: 'Their move',
      muster: 'Choosing the squadron',
      wind: 'The wind',
      battle: 'Manoeuvre and fire',
      over: 'Game over',
    },
    prompt: {
      handover: 'Pass the device to {{side}}.',
      portMove: 'Tap a highlighted berth to move the ship within the port. That is its move for this turn.',
      replay: 'The opponent’s last turn, step by step.',
      waiting: '{{side}} is making their move. You will see it as it happens.',
      musterEmpty: 'Tap ships on the map to add them to the squadron.',
      musterChosen: 'Ships to sail: {{n}}. Cast the wind when ready.',
      wind: 'The wind is cast…',
      battleMove: 'Tap a ship with ⛵ to move it, or select a ship and tap an enemy to fire.',
      battleReach: 'Tap a highlighted cell to sail there.',
      battleIdle: 'Fire, or end your turn.',
      endSummary: 'Some of your ships and forts have not acted yet.',
      fire: 'Choose who fires, then fire.',
      over: 'The game is over.',
    },
    action: {
      cast: 'Cast the wind ({{n}} ships)',
      castWind: 'Cast the wind',
      portMove: 'Move within port',
      portMoveCancel: 'Cancel the move in port',
      portMoveHint: 'Uses this ship’s move for the turn: it won’t sail with the squadron.',
      skipSailing: 'Skip sailing, go to firing',
      raiseSails: 'Raise sails',
      endTurn: 'End turn',
      keepPlaying: 'Keep playing',
      undo: 'Undo',
      undoNothing: 'Nothing to undo since the last wind, shot or turn.',
      allToSea: 'All to sea',
      allExceptPort: 'All except in port',
      clear: 'Clear',
      startTurn: 'Start turn',
      skipAnimation: 'Continue',
      noReturn: 'Can’t be undone',
      addToSquadron: 'Add to squadron',
      removeFromSquadron: 'Remove from squadron',
    },
    confirmCast: {
      title: 'Cast the wind?',
      body: 'The wind is cast once per turn, for the whole squadron. Ships not in the squadron can’t sail this turn — they can still fire. This can’t be undone.',
      confirm: 'Cast the wind',
      back: 'Back',
      dontAsk: 'Don’t ask again',
    },
    handover: {
      title: '{{side}}’s turn',
      first: '{{side}} moves first.',
      firstByLot: 'The lot has fallen: {{side}} moves first.',
    },
    wind: {
      fresh: 'Fresh wind to the {{dir}}: ships sail fastest downwind.',
      calm: 'Calm: only oared and steam ships can move.',
      storm: 'Storm to the {{dir}}: the squadron is driven before it.',
      movers: 'Ships able to sail: {{n}} of {{total}}.',
      title: 'The wind',
    },
    weather: {
      fresh: 'Fresh wind',
      calm: 'Calm',
      storm: 'Storm',
    },
    dir: {
      N: 'north',
      NE: 'north-east',
      E: 'east',
      SE: 'south-east',
      S: 'south',
      SW: 'south-west',
      W: 'west',
      NW: 'north-west',
    },
    fire: {
      inSights: 'In your sights: shooters {{n}}, shots {{shots}}',
      title: 'Fire at {{target}}',
      holes: 'Holes: {{n}} of {{max}}',
      fortHits: 'Hits: {{n}} of {{max}}',
      shots: 'shots: {{n}}',
      distance: 'distance {{n}}',
      fewer: 'Fewer shots from {{unit}}',
      more: 'More shots from {{unit}}',
      staysInPort: 'will have to stay in port this turn',
      sinks: 'a hit would sink her',
      holesAfter: 'a hit: {{n}} holes of {{max}}',
      destroys: 'a hit would destroy the fort',
      hitsAfter: 'a hit: {{n}} hits of {{max}}',
      volley: 'Volley ({{n}} guns)',
      oneByOne: 'Fire one by one',
      shoot: 'Fire',
      stop: 'Stop',
      close: 'Close',
      noReturn: 'Shots can’t be undone',
      hit: 'Hit!',
      miss: 'Miss.',
      none: 'Choose who fires.',
    },
    unit: {
      named: '“{{name}}”',
      ship: '{{cls}} #{{n}}',
      fortFirst: 'First-line fort {{n}}',
      fortSecond: 'Second-line fort {{n}}',
      fort: 'Fort {{name}}',
      fortClass: {
        first_line: 'First-line fort',
        second_line: 'Second-line fort',
      },
    },
    log: {
      title: 'Log',
      turn: 'Turn {{n}} · {{side}}',
      wind: '{{side}} cast the wind for {{n}} ships: {{weather}}, to the {{dir}}.',
      drift: '{{ship}} was driven by the storm {{from}} → {{to}}.',
      sailed: '{{ship}} sailed {{from}} → {{to}}.',
      inPort: '{{ship}} moved within the port to {{to}}.',
      anchored: '{{ship}} was driven ashore at {{cell}} and anchored.',
      wrecked: '{{ship}} was wrecked on the reef at {{cell}}.',
      shot: '{{shooter}} fired at {{target}}: {{result}}.',
      volley: 'Volley of {{n}} guns by {{shooters}} at {{target}}: {{result}}.',
      miss: 'miss',
      hit: 'hit',
      holes: 'hit, {{n}} of {{max}} holes',
      sunk: 'hit, sunk',
      fortHits: 'hit, {{n}} of {{max}}',
      destroyed: 'hit, the fort is destroyed',
      sunkInDock: '{{ship}} went down in dock with the fort.',
      dock: '{{ship}} entered dock: {{n}} turns of repair.',
      repaired: '{{ship}} repaired: {{n}} holes left.',
      dockLeft: '{{ship}} left dock, ready for sea.',
      fortRepair: '{{side}} began repairing {{fort}}.',
      fortRepairStopped: {
        voluntary: 'Repair of {{fort}} was stopped.',
        crew_lost: 'Repair of {{fort}} stopped: the crew was lost.',
        fort_destroyed: 'Repair of {{fort}} stopped: the fort was destroyed.',
        completed: 'Repair of {{fort}} is complete.',
      },
      fortRepaired: '{{fort}} repaired: {{n}} hits left.',
      captured: '{{fort}} now flies the flag of {{side}}.',
      royalHold: '{{side}} holds the enemy Royal Port: {{n}} of {{max}}.',
      royalLost: '{{side}} lost hold of the enemy Royal Port.',
      drawOffered: '{{side}} offered a draw.',
      drawExpired: 'The draw offer of {{side}} lapsed.',
      drawAccepted: '{{side}} accepted the draw.',
      resigned: '{{side}} resigned.',
      endTurn: '{{side}} ended the turn.',
      show: 'Show on the map',
      empty: 'Nothing has happened yet.',
      filter: {
        all: 'All',
        wind: 'Wind',
        move: 'Movement',
        fire: 'Fire',
        repair: 'Repair and capture',
        turn: 'Turns',
      },
      export: 'Export',
    },
    roster: {
      title: 'Fleet',
      mine: 'Your fleet',
      theirs: 'Opponent',
      forts: 'Forts',
      afloat: '{{n}} of {{total}} afloat',
      filter: {
        label: 'Show',
        canAct: 'Can act',
        all: 'All',
        squadron: 'Squadron',
        damaged: 'Damaged',
        inPort: 'In port',
      },
      status: {
        inSquadron: 'in squadron',
        toMove: 'to sail',
        moved: 'moved',
        fired: 'fired',
        shots: 'shots {{n}}',
        dock: 'in dock {{n}}',
        crew: 'repair crew',
        inPort: 'in port',
        sunk: 'sunk',
        destroyed: 'destroyed',
      },
      none: 'No ships match.',
    },
    view: {
      tools: 'View',
      myFleet: 'My fleet',
      chart: 'Chart view',
      perspective: 'Perspective',
      log: 'Log',
      keys: 'Keys',
      show: 'Show',
    },
    keys: {
      title: 'Keyboard',
      close: 'Close',
      space: 'Cast the wind · skip an animation',
      tab: 'Next / previous ship that can act',
      arrows: 'Move the cell cursor (with a ship selected) · pan',
      enter: 'Cast the wind · sail to the cursor · fire · end the turn',
      f: 'Aim the selected ship at its next target',
      v: 'Volley: everyone at the target',
      s: 'Add the selected ship to the squadron, or take it out',
      a: 'Every ship that may sail into the squadron, or none',
      z: 'Undo',
      r: 'Watch the opponent’s last turn',
      i: 'The selected unit’s card',
      esc: 'Close · deselect',
      m: 'My fleet',
      c: 'Chart view',
      l: 'Log',
      plusMinus: 'Zoom',
      help: 'This list',
    },
    replay: {
      watch: 'Watch {{side}}’s turn',
      title: 'Replay: {{side}}’s turn',
      step: 'Step {{n}} of {{total}}',
      pause: 'Pause',
      play: 'Play',
      skip: 'Skip',
      speed: 'Speed',
    },
    caption: {
      portMoved: 'Moved in port',
      wrecked: 'Wrecked!',
      sunk: 'Sunk!',
      destroyed: 'Fort destroyed!',
      repaired: 'Repaired: {{n}} left',
    },
    board: {
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      fit: 'Whole map',
      minimapShow: 'Show the minimap',
      minimapHide: 'Hide the minimap',
    },
    chart: {
      fort: 'Fort',
      royalPort: 'Royal Port',
    },
    sheet: {
      close: 'Close',
      view: 'View',
      picker: 'Which ship?',
      more: 'More',
      card: 'Card',
    },
    hud: {
      status: 'Turn status',
      tools: 'Game menus',
      fleet: 'Fleet',
      log: 'Log',
      minimap: 'Minimap',
      menu: 'Menu',
      help: 'Help',
      ships: 'Ships afloat: {{n}} of {{total}}',
      forts: 'Forts standing: {{n}} of {{total}}, damaged: {{damaged}}',
      royalHold: 'Holding the enemy Royal Port: {{n}} of {{max}}',
      settings: 'Settings',
    },
    inspector: {
      state: 'Now',
      specs: 'Class',
      holes: 'Holes',
      holesValue: '{{n}} of {{max}}',
      shotsLeft: 'Shots',
      shotsValue: '{{n}} of {{max}} left',
      sailing: 'Sailing',
      moveAvailable: 'Up to {{n}} cells',
      moveDone: 'Done this turn',
      moveRepair: 'Under repair',
      repair: 'Repair',
      repairDock: 'In dock: {{n}} more turn(s)',
      repairCrew: 'Crew repairing {{fort}}',
      repairFort: 'Fort under repair: {{n}} more turn(s)',
      squadron: 'Squadron',
      guns: 'Guns',
      shotsPerTurn: 'Shots / turn',
      range: 'Range',
      cells: '{{n}} cells',
      fresh: 'Fresh wind',
      calm: 'Calm',
      storm: 'Storm drift',
      noCalm: '—',
      sinksAt: 'Sinks at',
      sinksAtValue: '{{n}} holes',
      hits: 'Hits',
      expand: 'Details',
      collapse: 'Fewer details',
      close: 'Close',
      sunk: 'Sunk',
      destroyed: 'Destroyed',
      moveNoSquadron: 'Not in the squadron',
      moveAfterWind: 'After the wind',
      moveBecalmed: 'Becalmed',
      moveStorm: 'Drifted by the storm',
      moveBlocked: 'No way open',
      moveNotNow: 'Not now',
    },
    unitPanel: {
      holes: 'Holes: {{n}} of {{max}}',
      hits: 'Hits: {{n}} of {{max}}',
      royalHold: 'Royal Port: held {{n}} of {{max}}, {{left}} to go',
      shooters: 'Your units in range: {{n}}',
      outOfReach: 'Out of range of all your ships and forts.',
      move: 'Move',
      fire: 'Fire',
      fireAt: 'Fire at her',
      centre: 'Centre',
      deselect: 'Deselect',
      dock: 'Repair in dock',
      fortRepair: 'Repair the fort ({{n}} ships)',
      fortRepairStop: 'Stop the repair',
      enemy: 'enemy',
      note: {
        afterWind: 'Sails once the wind is cast.',
        chooseToSail: 'Add her to the squadron to sail this turn.',
        noTargets: 'No enemy in range.',
        noRoom: 'Nowhere to go from here.',
      },
    },
    reach: {
      blocked: '{{cell}}: {{why}}',
    },
    menu: {
      title: 'Menu',
      newGame: 'New game…',
      settings: 'Settings',
      game: 'Game',
      developer: 'Developer',
      chart: 'Chart view (flat, easier to tap)',
      watercolour: 'Watercolour map',
      confirmCast: 'Ask before casting the wind',
      offerDraw: 'Offer a draw',
      acceptDraw: '{{side}} accepts the draw',
      resign: '{{side}} resigns',
    },
    size: {
      label: 'Game size',
      start: 'Start: {{size}}',
      small: { name: 'Small game', counts: '6 ships / 2 forts', blurb: 'A compact sea battle.' },
      medium: {
        name: 'Medium game',
        counts: '12 ships / 4 forts',
        blurb: 'A tactical battle of several squadrons.',
      },
      large: { name: 'Large game', counts: '28 ships / 6 forts', blurb: 'A full-scale naval campaign.' },
      classic: 'Classic map',
    },
    help: {
      title: 'Help',
      turn: 'How a turn goes',
      steps: {
        muster: {
          title: 'Choose the squadron',
          body: 'tap your ships on the map to name who sails this turn; a check marks each, a second tap takes it out.',
        },
        wind: {
          title: 'Cast the wind',
          body: 'one spin for the whole squadron: fresh wind, calm or storm, and its direction.',
        },
        battle: {
          title: 'Manoeuvre and fire',
          body: 'select a ship: blue cells show where it can sail, red marks what it can hit. Move and fire in either order.',
        },
        end: { title: 'End the turn', body: 'and the other side takes its turn.' },
      },
      select: 'Two ships in one cell',
      selectBody:
        'A tap on a cell with two ships opens a small list: pick the one you mean. The chosen ship is ringed in blue, an enemy in red.',
    },
    chartHint: {
      title: 'Switch to the chart?',
      body: 'On a small screen the flat chart makes cells easier to tap. You can switch back any time with “Chart view”.',
      switch: 'Switch to the chart',
      keep: 'Keep the perspective',
    },
    summary: {
      title: 'End the turn?',
      notMoved: 'Still to sail: {{n}}',
      notFired: 'Can still fire: {{n}}',
    },
    squadron: {
      title: 'Squadron',
      in: 'In the squadron',
      out: 'Not sailing this turn',
      cannot: 'Can’t sail this turn',
      count: 'Squadron: {{n}} of {{total}}',
      list: 'Choose from the list',
    },
    over: {
      winner: '{{side}} wins',
      victory: 'Victory!',
      defeat: 'Defeat',
      end: 'The game is over',
      turn: 'Turn',
      afloat: '{{side}} afloat',
      close: 'Look at the chart',
      leave: 'Exit',
      won: '{{side}} wins',
      royal_port: 'The Royal Port was held.',
      annihilation: 'Every enemy ship was sunk.',
      resignation: 'The opponent resigned.',
      draw: 'Draw by agreement',
    },
  },
  why: {
    generic: 'Not allowed by the rules ({{rule}}).',
    gameOver: 'The game is over.',
    invalidAction: 'That can’t be done.',
    unknownUnit: 'There is no such ship or fort.',
    notYourTurn: 'It is the other side’s turn.',
    notYourUnit: 'That is not yours to command.',
    noDrawOffer: 'There is no draw offer to accept.',
    groupAlreadyCast: 'The wind has already been cast this turn.',
    invalidGroup: 'Choose at least one ship to sail.',
    noSquadron: 'Choose the squadron before casting the wind.',
    windNotCast: 'Cast the wind first: ships sail only after it.',
    castWindFirst: 'Cast the wind for the chosen squadron first.',
    notInSquadron: 'This ship is not in the squadron, so it can’t sail this turn. It can still fire.',
    inSquadron: 'Ships in the squadron sail with the wind, not inside the port.',
    stormDrifted: 'The storm has already carried this ship: that was its move.',
    becalmed: 'Becalmed: a sailing ship can’t move without wind.',
    sunk: 'This ship has been sunk.',
    inDock: 'This ship is in dock under repair.',
    alreadyInDock: 'This ship is already in dock.',
    onFortRepair: 'This ship is repairing a fort.',
    alreadyMoved: 'This ship has already made its move this turn.',
    firedInPort: 'This ship fired from the port and must stay there this turn.',
    notInLine: 'Ships move and fire only in straight lines: along a row, a column or a diagonal.',
    tooFar: 'Too far against this wind.',
    blockedByLand: 'Land is in the way.',
    blockedByReef: 'A reef is in the way.',
    ownRoyalPort: 'Your own ships may not enter your Royal Port.',
    enemyPort: 'An enemy fort guards this port: destroy it first.',
    cellFull: 'There is no room in that cell.',
    portFull: 'The port is full.',
    diagonalSqueeze: 'The passage between the two shores is too narrow.',
    shipInTheWay: 'Another ship is in the way.',
    notInOwnPort: 'This ship is not in one of your defended ports.',
    notInThisPort: 'A ship may move only to another berth of the same port.',
    invalidShots: 'Each shooter must fire at least one shot.',
    noShotsLeft: 'No shots left this turn.',
    cannotFireInDock: 'A ship in dock can’t fire.',
    cannotFireOnRepair: 'A ship repairing a fort can’t fire.',
    onEnemyRoyalPort: 'A ship standing on the enemy Royal Port can’t fire.',
    fortDestroyed: 'The fort is destroyed.',
    outOfRange: 'Out of range.',
    lineOfFireBlocked: 'Land blocks the line of fire.',
    friendlyTarget: 'That is one of your own.',
    fortCannotTargetFort: 'Forts fire only at ships.',
    targetGone: 'The target is already sunk or destroyed.',
    volleyTooFewShooters: 'A volley needs at least {{n}} ships or forts.',
    notDamaged: 'Nothing to repair: no damage.',
    firedThisTurn: 'A ship that fired this turn can’t start repairs.',
    alreadyRepairing: 'Already under repair.',
    enemiesInPort: 'Enemy ships are in the port.',
    notYourFort: 'That fort is not yours.',
    wrongCrewSize: 'A fort repair needs a crew of exactly {{n}} ships.',
    crewNotInPort: 'The crew must stand in the fort’s port.',
    noRepairHere: 'You are not repairing this fort.',
  },
  learn: {
    pageTitle: 'How to play',
    title: 'How to play',
    lead: 'The rules of Salt & Shot in short: one page for each part of the game.',
    open: 'How to play',
    contents: 'Contents',
    picture: 'An example on the board: {{topic}}',
    rules: 'Rules: {{ids}}',
    back: 'Back to the harbour',
    hintMore: 'More…',
    hintGotIt: 'Got it',
    hintsOn: 'Show hints',
    hintsReset: 'Show all hints again',
    hints: {
      muster: {
        title: 'Choose your squadron',
        body: 'Tap the ships that will go this turn, then cast the wind. Only the squadron moves after the wind; the others may still fire.',
      },
      wind: {
        title: 'The wind is cast',
        body: 'Select a ship of the squadron: blue cells show where she can go. A fresh wind is fastest downwind, where the arrow points; each 45° into the wind costs a cell.',
      },
      becalmed: {
        title: 'Not every ship can move',
        body: 'In calm only oared and steam ships move, and a headwind may leave a sailing ship nowhere to go. She can still fire.',
      },
      storm: {
        title: 'Storm!',
        body: 'The storm drove every ship of the squadron downwind: that was their move this turn. Ships left out of the squadron stayed where they were.',
      },
      fire: {
        title: 'An enemy in range',
        body: 'Select your ship, then tap the enemy marked red to fire. Each shot hits with a chance of {{hitPercent}}%.',
      },
      volley: {
        title: 'A volley is possible',
        body: '{{volleyMin}} or more ships can fire at one target together: one roll for all of them, and on a hit every shot strikes at once.',
      },
      damaged: {
        title: 'A hole in the hull',
        body: 'A ship sinks when her holes reach the number on her card. In her own port she can be mended in dock.',
      },
      dock: {
        title: 'Into dock',
        body: 'A damaged ship in her own port may go into dock if she has not fired this turn: one hole is mended at the end of each of your turns. In dock she neither moves nor fires.',
      },
      fort: {
        title: 'Under a fort’s guns',
        body: 'An enemy fort fires at ships within its range in its own side’s turn. Keep clear, or bring several ships to knock it down.',
      },
      royalPort: {
        title: 'On a Royal Port',
        body: 'A ship standing on the enemy’s Royal Port from the start to the end of her side’s turn adds one to the count. At {{hold}} that side wins.',
      },
    },
    topics: {
      goal: {
        title: 'The goal',
        body: 'Two fleets of sail and early steam meet on a chart of harbours, forts and reefs. You win in one of two ways: sink every enemy ship, or hold the enemy’s Royal Port for {{hold}} turns in a row.\n\nEvery ship and fort stands on the map. Select one to see its card: speed in fresh wind and in calm, guns, range, and how many holes it can take.',
      },
      turn: {
        title: 'A turn',
        body: 'The sides take turns. Your turn has three steps.\n\n1. Choose the squadron: tap the ships that will sail this turn, then “Cast the wind”. The wind decides where they can go and how far.\n\n2. Move and fire in any order. Select a ship: blue cells are where she can go, red marks are whom she can hit. Ships outside the squadron may fire too.\n\n3. Press “End turn”. Ships in dock and on fort repair are mended, a hold on the enemy’s Royal Port is counted, and the enemy moves.\n\nThe line at the top of the screen always says what to do now, and the round button in the bottom right corner does that step: “Cast the wind”, then “End turn”.',
      },
      muster: {
        title: 'The squadron',
        body: 'Before the wind is cast you name the squadron: the ships that will sail this turn. Tap them on the map; a check marks each one. Only the squadron moves after the wind — and only the squadron is caught by a storm.\n\nYou may also skip sailing and go straight to firing. Ships in dock or repairing a fort cannot join the squadron.',
      },
      wind: {
        title: 'The wind',
        body: 'One cast of the wind spinner serves the whole squadron. It gives a direction and a weather: fresh wind, calm or storm, each as likely as the others.\n\nIn fresh wind a ship sails fastest downwind, the way the arrow points, and each 45° it turns into the wind costs a cell. The brig in the picture makes {{brigFast}} cells downwind and {{brigSlow}} straight against it. Ships with oars or steam never fall below their speed in calm.',
      },
      movement: {
        title: 'Moving',
        body: 'Every move is a straight line in one of eight directions, across or diagonally, as far as the ship’s reach. Land, forts and reefs stop it. A ship may pass other ships but must end where there is room: two ships to a sea cell, one to a coast cell, no more than {{portLimit}} in a port.\n\nIn calm only ships with oars or steam move, at their calm speed, in any direction; sailing ships wait for wind. Each ship moves once a turn.',
      },
      storm: {
        title: 'Storm',
        body: 'A storm drives every ship of the squadron downwind by its storm drift, at once and whether you like it or not. The drift is the ship’s move for the turn.\n\nA ship blown onto a reef is wrecked. One driven against land or a full cell stops short and anchors. Ships outside the squadron ride the storm out where they are, so a small squadron is the safer bet near reefs.',
      },
      fire: {
        title: 'Fire and volley',
        body: 'Ships and forts fire in any of the eight directions, as far as their range. Land and forts in between block the shot; ships, reefs and coast do not. Each shot hits with a chance of {{hitPercent}}% and makes one hole; a ship sinks when her holes reach the number on her card.\n\nA volley is {{volleyMin}} or more ships firing at one target together. Whether it hits is decided once for all of them, with the same {{hitPercent}}% chance: a hit lands every hole at once, a miss wastes every shot. Use it to sink a big ship at a blow. Shots not fired are lost at the end of the turn.',
      },
      ports: {
        title: 'Ports and dock',
        body: 'Enemy ships may not enter your port while its fort stands. Inside your own port a ship may move to any other port cell without the wind; that is its move for the turn.\n\nA damaged ship in its own port may go into dock if it has not fired this turn. The turn it docks does not count; after that it mends one hole at the end of each of your turns and leaves dock when whole. In dock it cannot move or fire but can still be hit, and if the fort falls, every ship in its dock sinks.',
      },
      forts: {
        title: 'Forts',
        body: 'A first-line fort fires {{fort1Shots}} shots a turn up to {{fort1Range}} cells and falls after {{fort1Hits}} hits; a second-line fort fires {{fort2Shots}} up to {{fort2Range}} and falls after {{fort2Hits}}. Forts fire only in their own side’s turn.\n\n{{crew}} of your ships in the port of a damaged fort, none of which fired this turn, can repair it: {{fortRepair}} hits a turn, from the next turn on. A fallen fort leaves its port open sea, and whoever repairs it with {{crew}} ships, with no enemy in the port, raises their own flag over it.',
      },
      royal: {
        title: 'The Royal Port',
        body: 'Each side has one Royal Port, with no fort. Your own ships may not enter it; the enemy’s may, two at a time.\n\nA ship standing on the enemy’s Royal Port cannot fire but can be fired upon. If one of your ships stands there from the start to the end of your turn, the count goes up by one; if none does, it starts again. The turn you arrive does not count. At {{hold}} you win.\n\nA storm drives only the squadron. Leave the ship holding the port out of it and no storm will touch her. If she is in the squadron, a storm drives her off the port like any other, and unless another of your ships stays there, the count starts again.',
      },
      end: {
        title: 'End of the game',
        body: 'The game ends when a side has no ships afloat, sunk and wrecked alike, or when a side holds the enemy’s Royal Port for {{hold}} turns.\n\nA side may also resign at any time, or offer a draw: the offer stands until the end of the opponent’s next turn. Both are in the game menu. A finished game stays in your logbook, where you can watch its last turn again.',
      },
    },
  },
  shipsPage: {
    pageTitle: 'Ships',
    title: 'Ships',
    lead: 'Every class in the fleet: how she sails and how she fights.',
    intro:
      'Each side sails the same fleet. A ship’s class sets how far she goes in each weather and how she fires; the numbers are the ones on her card.',
    movement: {
      title: 'Movement',
      body: 'Cells per move. In a fresh wind the speed is straight downwind, one cell less for each 45° into the wind; oared and steam ships may use their calm speed instead if it is better. In calm only oared and steam ships move. A storm drives a ship downwind by her drift.',
    },
    combat: {
      title: 'Combat',
      body: 'A ship fires her shots each turn at enemies up to her range, and sinks when her holes reach the number under “Holes to sink”.',
    },
    table: {
      ship: 'Ship',
      propulsion: 'Driven by',
      guns: 'Guns',
      inFleet: 'In the fleet',
      kinds: { sail: 'sail', oars: 'sail and oars', steam: 'sail and steam' },
    },
  },
  credits: {
    pageTitle: 'Credits',
    title: 'Credits',
    artwork: 'Artwork',
    ownArt: 'Vector ship tokens and board art: drawn for Salt & Shot.',
    link: 'Credits',
  },
  footer: {
    scale: 'Nautical Miles',
    imprint: 'Charted with care. Not to be used for navigation.',
  },
} as const;

type Widen<T> = { [K in keyof T]: T[K] extends string ? string : Widen<T[K]> };
export type Messages = Widen<typeof en>;
